<template>
	<div v-if="isDevMode" class="dev-tools-panel" :class="{ collapsed: isCollapsed }">
		<!-- Кнопка сворачивания/разворачивания -->
		<button class="toggle-button" @click="togglePanel" :title="isCollapsed ? 'Развернуть панель разработчика' : 'Свернуть панель разработчика'">
			<span v-if="isCollapsed">🔧</span>
			<span v-else>✖</span>
		</button>

		<!-- Содержимое панели -->
		<div v-if="!isCollapsed" class="panel-content">
			<!-- Заголовок -->
			<div class="panel-header">
				<h3>🔧 Developer Mode</h3>
				<span class="dev-badge">DEV</span>
			</div>

			<!-- Текущий профиль -->
			<div class="current-profile">
				<div class="profile-label">Текущий профиль:</div>
				<div class="profile-value" :class="`profile-${currentRole}`">
					{{ currentRoleLabel }}
				</div>
			</div>

			<!-- Кнопки переключения профилей -->
			<div class="profile-buttons">
				<button
					class="profile-btn student-btn"
					:class="{ active: currentRole === 'student' }"
					@click="switchProfile('student')"
					title="Переключиться на профиль студента (Ctrl+1)"
				>
					<span class="btn-icon">👨‍🎓</span>
					<span class="btn-label">Студент</span>
				</button>

				<button
					class="profile-btn teacher-btn"
					:class="{ active: currentRole === 'teacher' }"
					@click="switchProfile('teacher')"
					title="Переключиться на профиль преподавателя (Ctrl+2)"
				>
					<span class="btn-icon">👨‍🏫</span>
					<span class="btn-label">Преподаватель</span>
				</button>

				<button
					class="profile-btn admin-btn"
					:class="{ active: currentRole === 'admin' }"
					@click="switchProfile('admin')"
					title="Переключиться на профиль администратора (Ctrl+3)"
				>
					<span class="btn-icon">👨‍💼</span>
					<span class="btn-label">Админ</span>
				</button>
			</div>

			<!-- Дополнительные действия -->
			<div class="actions">
				<button class="action-btn clear-btn" @click="clearAuth" title="Очистить авторизацию">
					🗑️ Очистить
				</button>
				<button class="action-btn reload-btn" @click="reloadPage" title="Перезагрузить страницу">
					🔄 Reload
				</button>
			</div>

			<!-- Горячие клавиши -->
			<div class="hotkeys">
				<div class="hotkeys-title">Горячие клавиши:</div>
				<div class="hotkey-item">
					<kbd>Ctrl</kbd> + <kbd>1</kbd> - Студент
				</div>
				<div class="hotkey-item">
					<kbd>Ctrl</kbd> + <kbd>2</kbd> - Преподаватель
				</div>
				<div class="hotkey-item">
					<kbd>Ctrl</kbd> + <kbd>3</kbd> - Админ
				</div>
				<div class="hotkey-item">
					<kbd>Ctrl</kbd> + <kbd>D</kbd> - Свернуть/развернуть
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/stores/useAppStore'
import { isDevModeEnabled } from '@/utils/devMode'
import { setMockUser, getCurrentMockUserRole, clearMockUser } from '@/utils/mockUsers'

export default {
	name: 'DevToolsPanel',
	setup() {
		const router = useRouter()
		const appStore = useAppStore()

		const isDevMode = ref(isDevModeEnabled())
		const isCollapsed = ref(localStorage.getItem('dev_panel_collapsed') === 'true')
		const currentRole = ref(getCurrentMockUserRole())

		const currentRoleLabel = computed(() => {
			const labels = {
				student: '👨‍🎓 Студент',
				teacher: '👨‍🏫 Преподаватель',
				admin: '👨‍💼 Администратор',
			}
			return labels[currentRole.value] || '❓ Не выбран'
		})

		/**
		 * Переключение профиля
		 */
		const switchProfile = async (role) => {
			try {
				console.log(`🔧 [DevTools] Switching to ${role} profile...`)

				// Устанавливаем mock-пользователя
				await setMockUser(role, appStore)

				// Обновляем текущую роль
				currentRole.value = role

				// Показываем уведомление
				appStore.showSuccess(`Профиль переключен: ${role}`)

				// Перенаправляем на соответствующий дашборд
				const routes = {
					student: '/student-dashboard',
					teacher: '/teacher/dashboard',
					admin: '/admin/dashboard',
				}

				await router.push(routes[role])

				console.log(`✅ [DevTools] Switched to ${role} profile successfully`)
			} catch (error) {
				console.error('❌ [DevTools] Error switching profile:', error)
				appStore.showError('Ошибка при переключении профиля')
			}
		}

		/**
		 * Очистка авторизации
		 */
		const clearAuth = () => {
			clearMockUser()
			appStore.logout()
			currentRole.value = null
			appStore.showInfo('Авторизация очищена')
			router.push('/auth')
		}

		/**
		 * Перезагрузка страницы
		 */
		const reloadPage = () => {
			window.location.reload()
		}

		/**
		 * Сворачивание/разворачивание панели
		 */
		const togglePanel = () => {
			isCollapsed.value = !isCollapsed.value
			localStorage.setItem('dev_panel_collapsed', isCollapsed.value.toString())
		}

		/**
		 * Обработчик горячих клавиш
		 */
		const handleKeydown = (event) => {
			// Ctrl+1 - Студент
			if (event.ctrlKey && event.key === '1') {
				event.preventDefault()
				switchProfile('student')
			}
			// Ctrl+2 - Преподаватель
			else if (event.ctrlKey && event.key === '2') {
				event.preventDefault()
				switchProfile('teacher')
			}
			// Ctrl+3 - Админ
			else if (event.ctrlKey && event.key === '3') {
				event.preventDefault()
				switchProfile('admin')
			}
			// Ctrl+D - Свернуть/развернуть панель
			else if (event.ctrlKey && event.key === 'd') {
				event.preventDefault()
				togglePanel()
			}
		}

		onMounted(() => {
			// Добавляем обработчик горячих клавиш
			window.addEventListener('keydown', handleKeydown)

			// Обновляем текущую роль при монтировании
			currentRole.value = getCurrentMockUserRole()
		})

		onUnmounted(() => {
			// Удаляем обработчик горячих клавиш
			window.removeEventListener('keydown', handleKeydown)
		})

		return {
			isDevMode,
			isCollapsed,
			currentRole,
			currentRoleLabel,
			switchProfile,
			clearAuth,
			reloadPage,
			togglePanel,
		}
	},
}
</script>

<style scoped>
/* Основной контейнер панели */
.dev-tools-panel {
	position: fixed;
	bottom: 20px;
	right: 20px;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	border-radius: 12px;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
	z-index: 9999;
	font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
	transition: all 0.3s ease;
	min-width: 280px;
}

.dev-tools-panel.collapsed {
	min-width: auto;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Кнопка сворачивания */
.toggle-button {
	position: absolute;
	top: -12px;
	right: -12px;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: #ff6b6b;
	border: 3px solid white;
	color: white;
	font-size: 16px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.toggle-button:hover {
	transform: scale(1.1);
	background: #ff5252;
}

/* Содержимое панели */
.panel-content {
	padding: 20px;
	color: white;
}

/* Заголовок */
.panel-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 15px;
	padding-bottom: 10px;
	border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}

.panel-header h3 {
	margin: 0;
	font-size: 18px;
	font-weight: 600;
}

.dev-badge {
	background: #ff6b6b;
	padding: 4px 10px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: bold;
	letter-spacing: 1px;
}

/* Текущий профиль */
.current-profile {
	margin-bottom: 15px;
	padding: 10px;
	background: rgba(255, 255, 255, 0.1);
	border-radius: 8px;
}

.profile-label {
	font-size: 12px;
	opacity: 0.8;
	margin-bottom: 5px;
}

.profile-value {
	font-size: 16px;
	font-weight: 600;
}

/* Кнопки профилей */
.profile-buttons {
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin-bottom: 15px;
}

.profile-btn {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 15px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.1);
	color: white;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
}

.profile-btn:hover {
	background: rgba(255, 255, 255, 0.2);
	transform: translateX(5px);
}

.profile-btn.active {
	background: rgba(255, 255, 255, 0.3);
	border-color: white;
	box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
}

.btn-icon {
	font-size: 20px;
}

.btn-label {
	flex: 1;
	text-align: left;
}

/* Дополнительные действия */
.actions {
	display: flex;
	gap: 8px;
	margin-bottom: 15px;
}

.action-btn {
	flex: 1;
	padding: 8px 12px;
	border: 1px solid rgba(255, 255, 255, 0.3);
	border-radius: 6px;
	background: rgba(255, 255, 255, 0.1);
	color: white;
	font-size: 12px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.action-btn:hover {
	background: rgba(255, 255, 255, 0.2);
}

/* Горячие клавиши */
.hotkeys {
	padding: 10px;
	background: rgba(0, 0, 0, 0.2);
	border-radius: 8px;
	font-size: 11px;
}

.hotkeys-title {
	font-weight: 600;
	margin-bottom: 8px;
	opacity: 0.9;
}

.hotkey-item {
	margin-bottom: 4px;
	opacity: 0.8;
}

kbd {
	background: rgba(255, 255, 255, 0.2);
	padding: 2px 6px;
	border-radius: 4px;
	font-family: monospace;
	font-size: 10px;
	border: 1px solid rgba(255, 255, 255, 0.3);
}
</style>

